import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  effectiveType: string;
}

/**
 * Hook to monitor network connectivity and connection quality.
 * Detects offline state and slow connections (2G, slow-2g).
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlowConnection, setIsSlowConnection] = useState(false);
  const [effectiveType, setEffectiveType] = useState('unknown');

  const updateConnectionInfo = useCallback(() => {
    // Check if online
    setIsOnline(navigator.onLine);

    // Check connection quality if available
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      const type = connection.effectiveType || 'unknown';
      setEffectiveType(type);
      
      // Consider 2G and slow-2g as slow connections
      const isSlow = type === '2g' || type === 'slow-2g';
      setIsSlowConnection(isSlow);
      
      console.log(`[NetworkStatus] Connection type: ${type}, slow: ${isSlow}`);
    }
  }, []);

  useEffect(() => {
    // Initial check
    updateConnectionInfo();

    // Listen for online/offline events
    window.addEventListener('online', updateConnectionInfo);
    window.addEventListener('offline', updateConnectionInfo);

    // Listen for connection changes if supported
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateConnectionInfo);
    }

    return () => {
      window.removeEventListener('online', updateConnectionInfo);
      window.removeEventListener('offline', updateConnectionInfo);
      if (connection) {
        connection.removeEventListener('change', updateConnectionInfo);
      }
    };
  }, [updateConnectionInfo]);

  return { isOnline, isSlowConnection, effectiveType };
};

export default useNetworkStatus;
